import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Service for handling referral-related operations with Firestore
 */
const referralService = {
  /**
   * Get a user's referral code
   * @param {string} userId - The user ID
   * @returns {Promise<string>} - The user's referral code
   */
  getUserReferralCode: async (userId) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        // Check if user already has a referral code
        if (userDoc.data().referralCode) {
          return userDoc.data().referralCode;
        }
        
        // Generate a new referral code based on user info
        const displayName = userDoc.data().displayName || '';
        const prefix = displayName.split(' ')[0] || 'user';
        const referralCode = `${prefix}${userId.substring(0, 6)}`.toUpperCase();
        
        // Save the referral code to the user document
        await updateDoc(userDocRef, {
          referralCode: referralCode,
          referralCodeCreatedAt: serverTimestamp()
        });
        
        return referralCode;
      } else {
        throw new Error("User document does not exist");
      }
    } catch (error) {
      console.error("Error getting referral code:", error);
      throw error;
    }
  },
  
  /**
   * Get all referrals for a user
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} - Array of referral objects
   */
  getUserReferrals: async (userId) => {
    try {
      const referralsRef = collection(db, 'referrals');
      const referralsQuery = query(
        referralsRef,
        where('referrerId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const referralsSnapshot = await getDocs(referralsQuery);
      
      if (referralsSnapshot.empty) {
        return [];
      }
      
      const referrals = [];
      
      for (const referralDoc of referralsSnapshot.docs) {
        const referralData = referralDoc.data();
        
        // Get referred user details
        let referredUserData = {};
        if (referralData.referredUserId) {
          const userDoc = await getDoc(doc(db, 'users', referralData.referredUserId));
          if (userDoc.exists()) {
            referredUserData = {
              name: userDoc.data().displayName || 'Anonymous User',
              email: userDoc.data().email || 'No email'
            };
          }
        } else {
          referredUserData = {
            name: referralData.invitedName || 'Invited User',
            email: referralData.invitedEmail || 'No email'
          };
        }
        
        // Check if the referred user has completed a campaign
        let hasCompletedCampaign = false;
        let bonus = 0;
        
        if (referralData.referredUserId) {
          const participantsRef = collection(db, 'campaignParticipants');
          const participantsQuery = query(
            participantsRef,
            where('influencerId', '==', referralData.referredUserId),
            where('status', '==', 'completed'),
            limit(1)
          );
          
          const participantsSnapshot = await getDocs(participantsQuery);
          hasCompletedCampaign = !participantsSnapshot.empty;
          
          // If completed campaign, check if bonus was already awarded
          if (hasCompletedCampaign && !referralData.bonusAwarded) {
            bonus = 500; // ₱500 bonus per successful referral
            
            // Update the referral document to mark bonus as awarded
            await updateDoc(doc(db, 'referrals', referralDoc.id), {
              bonusAwarded: true,
              bonusAmount: bonus,
              bonusAwardedAt: serverTimestamp()
            });
          } else if (referralData.bonusAwarded) {
            bonus = referralData.bonusAmount || 500;
          }
        }
        
        // Determine status
        let status = 'invited';
        if (referralData.referredUserId) {
          status = 'registered';
          
          // Check if user is active (has applied to at least one campaign)
          const applicationsRef = collection(db, 'campaignParticipants');
          const applicationsQuery = query(
            applicationsRef,
            where('influencerId', '==', referralData.referredUserId),
            limit(1)
          );
          
          const applicationsSnapshot = await getDocs(applicationsQuery);
          if (!applicationsSnapshot.empty) {
            status = 'active';
          }
          
          // Check if completed a campaign
          if (hasCompletedCampaign) {
            status = 'completed';
          }
        }
        
        referrals.push({
          id: referralDoc.id,
          name: referredUserData.name,
          email: referredUserData.email,
          status: status,
          date: referralData.createdAt?.toDate().toISOString() || new Date().toISOString(),
          bonus: bonus
        });
      }
      
      return referrals;
    } catch (error) {
      console.error("Error getting user referrals:", error);
      throw error;
    }
  },
  
  /**
   * Calculate total earnings from referrals
   * @param {string} userId - The user ID
   * @returns {Promise<number>} - Total earnings
   */
  calculateTotalEarnings: async (userId) => {
    try {
      const referralsRef = collection(db, 'referrals');
      const referralsQuery = query(
        referralsRef,
        where('referrerId', '==', userId),
        where('bonusAwarded', '==', true)
      );
      
      const referralsSnapshot = await getDocs(referralsQuery);
      
      if (referralsSnapshot.empty) {
        return 0;
      }
      
      let totalEarnings = 0;
      
      referralsSnapshot.forEach(doc => {
        totalEarnings += doc.data().bonusAmount || 0;
      });
      
      return totalEarnings;
    } catch (error) {
      console.error("Error calculating total earnings:", error);
      throw error;
    }
  },
  
  /**
   * Send a referral invitation
   * @param {string} userId - The referring user ID
   * @param {string} referralCode - The user's referral code
   * @param {string} email - Email of the person to invite
   * @param {string} message - Optional personal message
   * @returns {Promise<void>}
   */
  sendReferralInvitation: async (userId, referralCode, email, message = '') => {
    try {
      // Create a record of the invitation in Firestore
      await addDoc(collection(db, 'referrals'), {
        referrerId: userId,
        invitedEmail: email,
        referralCode: referralCode,
        personalMessage: message,
        status: 'invited',
        createdAt: serverTimestamp()
      });
      
      // In a real implementation, this would also trigger a cloud function
      // to send an actual email invitation with the referral link
      
      return true;
    } catch (error) {
      console.error("Error sending referral invitation:", error);
      throw error;
    }
  }
};

export default referralService;
